import { useEffect, useState } from 'react';
import { Form, Select, SelectProps } from 'antd';

import SelectSuffixIcon from '../../../components/SelectSuffixIcon/SelectSuffixIcon';
import { ClientService } from '../../../shared/api/ClientService';
import useLocale from '../../../hooks/useLocale';
import API from '../../../utils/api';
import { PdsUserClientAPI } from '../../../utils/identityServiceApi';
import { MAX_PAGE_SIZE } from '../../../constants/common';

interface WalkInAgentSelectProps extends SelectProps {
  roleNames: string[];
}

/**
 * Agent dropdown for walk-in appointments.
 * Fetches all active agents filtered by role (bypasses timeslot-based availability).
 */
const WalkInAgentSelect = ({ roleNames, ...props }: WalkInAgentSelectProps) => {
  const { t } = useLocale();
  const { Option } = Select;
  const [agents, setAgents] = useState<ClientService.AgentDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!roleNames?.length) return;

      setLoading(true);
      try {
        const result = await API.searchAgent(true, undefined, undefined, 0, MAX_PAGE_SIZE);
        const allAgents = result?.items || [];
        const allAgentIds = allAgents.map((a) => a.id).filter(Boolean) as string[];

        if (!allAgentIds.length) {
          setAgents([]);
          return;
        }

        // Batch filterUsers calls to avoid URL length limits
        const BATCH_SIZE = 100;
        const filteredIds: string[] = [];
        for (let i = 0; i < allAgentIds.length; i += BATCH_SIZE) {
          const batch = allAgentIds.slice(i, i + BATCH_SIZE);
          const batchResult = await PdsUserClientAPI.filterUsers(batch, '', roleNames, undefined);
          if (batchResult?.length) {
            filteredIds.push(...batchResult);
          }
        }

        const filteredAgents = allAgents.filter((a) => filteredIds.includes(a.id as string));
        setAgents(filteredAgents);
      } catch {
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [roleNames]);

  return (
    <Form.Item
      name="agentId"
      label={t.APPOINTMENT_SCHEDULING_AGENTS}
      className="SchedulingComponent__agents"
      rules={[{ required: true, message: t.AGENT_IS_REQUIRED }]}
    >
      <Select
        size="large"
        suffixIcon={<SelectSuffixIcon />}
        loading={loading}
        showSearch
        optionFilterProp="children"
        placeholder={t.APPOINTMENT_SCHEDULING_SELECT_AGENT}
        {...props}
      >
        {agents.map((agent) => (
          <Option key={agent.id} value={agent.id}>
            {agent.fullName || agent.id}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default WalkInAgentSelect;
